import { zodResolver } from '@hookform/resolvers/zod';
import { changePasswordSchema } from '@fitness/core';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Check, KeyRound, TriangleAlert, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Field, fieldAria } from '@/components/Field';
import { Input } from '@/components/Input';
import { LoginMethodsCard, SetPasswordCard } from '@/components/LoginMethodsCard';
import { PageHeader } from '@/components/PageHeader';
import { api, describeError, hasErrorCode } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { currentDevice } from '@/lib/device';
import { useLoginMethods } from '@/lib/socialLogin';

interface AccountSearch {
  /** Vuelta del proveedor para poner la primera contraseña. */
  setPassword?: boolean;
}

export const Route = createFileRoute('/_app/settings/account')({
  validateSearch: (search: Record<string, unknown>): AccountSearch => {
    const raw = search['setPassword'];
    return raw === 1 || raw === '1' || raw === true ? { setPassword: true } : {};
  },
  component: AccountPage,
});

/** El formulario pide la nueva dos veces; el servidor solo recibe una. */
const passwordFormSchema = z
  .object({
    currentPassword: z.string().min(1, 'Escribe tu contraseña actual.').max(128),
    newPassword: z
      .string()
      .min(8, 'La nueva tiene que tener al menos 8 caracteres.')
      .max(128, 'Como mucho 128 caracteres.'),
    repeatPassword: z.string().min(1, 'Repite la contraseña nueva.'),
  })
  .refine((value) => value.repeatPassword === value.newPassword, {
    path: ['repeatPassword'],
    message: 'Las dos contraseñas nuevas no coinciden.',
  })
  .refine((value) => value.newPassword !== value.currentPassword, {
    path: ['newPassword'],
    message: 'La contraseña nueva tiene que ser distinta de la actual.',
  });

type PasswordFormValues = z.infer<typeof passwordFormSchema>;

function AccountPage() {
  const search = Route.useSearch();
  const methods = useLoginMethods();
  // Mientras carga se pinta lo de siempre: casi todas las cuentas tienen
  // contraseña, y el servidor decide en cualquier caso.
  const hasPassword = methods.data?.hasPassword ?? true;

  return (
    <>
      <PageHeader
        title="Cuenta"
        subtitle="Cómo entras, tu contraseña y, si quieres irte, el borrado de la cuenta."
        action={
          <Link
            to="/settings"
            aria-label="Volver a Ajustes"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <div className="flex flex-col gap-4">
        <LoginMethodsCard />
        {methods.data && !methods.data.hasPassword ? (
          <SetPasswordCard methods={methods.data} confirmed={search.setPassword === true} />
        ) : (
          <ChangePasswordCard />
        )}
        <DeleteAccountCard hasPassword={hasPassword} />
      </div>
    </>
  );
}

// --- Cambiar contraseña -----------------------------------------------------

function ChangePasswordCard() {
  const [saved, setSaved] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: { currentPassword: '', newPassword: '', repeatPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSaved(false);
    setFormError(null);
    try {
      const body = changePasswordSchema.parse({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        device: currentDevice(),
      });
      await api.me.changePassword(body);
      reset();
      setSaved(true);
    } catch (error) {
      if (hasErrorCode(error, 'wrong_password')) {
        setError('currentPassword', { message: describeError(error) }, { shouldFocus: true });
        return;
      }
      setFormError(describeError(error));
    }
  });

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="size-4 text-brand" aria-hidden="true" />
          Cambiar contraseña
        </span>
      }
      description="Al cambiarla se cierra la sesión en todos tus otros dispositivos. Los tokens de acceso (MCP) siguen funcionando: si temes que alguien tenga uno, revócalo en Ajustes → Tokens."
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError ? <ErrorBanner title="No se ha podido cambiar" message={formError} /> : null}
        {saved ? (
          <p
            role="status"
            className="flex items-center gap-2 rounded-xl border border-brand/40 bg-surface p-3 text-sm"
          >
            <Check className="size-4 shrink-0 text-brand" aria-hidden="true" />
            Contraseña cambiada. Las demás sesiones se han cerrado.
          </p>
        ) : null}

        <Field
          htmlFor="currentPassword"
          label="Contraseña actual"
          error={errors.currentPassword?.message}
        >
          <Input
            {...fieldAria('currentPassword', errors.currentPassword?.message)}
            type="password"
            autoComplete="current-password"
            {...register('currentPassword')}
          />
        </Field>

        <Field
          htmlFor="newPassword"
          label="Contraseña nueva"
          error={errors.newPassword?.message}
          hint="Entre 8 y 128 caracteres."
        >
          <Input
            {...fieldAria('newPassword', errors.newPassword?.message, true)}
            type="password"
            autoComplete="new-password"
            {...register('newPassword')}
          />
        </Field>

        <Field
          htmlFor="repeatPassword"
          label="Repite la contraseña nueva"
          error={errors.repeatPassword?.message}
        >
          <Input
            {...fieldAria('repeatPassword', errors.repeatPassword?.message)}
            type="password"
            autoComplete="new-password"
            {...register('repeatPassword')}
          />
        </Field>

        <Button type="submit" size="lg" block loading={isSubmitting}>
          Cambiar contraseña
        </Button>
      </form>
    </Card>
  );
}

// --- Zona de peligro --------------------------------------------------------

function DeleteAccountCard({ hasPassword }: { hasPassword: boolean }) {
  const { deleteAccount } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  function cancel(): void {
    setConfirming(false);
    setPassword('');
    setPasswordError(null);
    setFormError(null);
  }

  async function handleDelete(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    if (password === '') {
      setPasswordError('Escribe tu contraseña para confirmar.');
      return;
    }
    setPasswordError(null);
    setDeleting(true);
    try {
      await deleteAccount(password);
      // Recarga entera en vez de navegar: la guarda de rutas ya reacciona a la
      // sesión perdida (y apuntaría a volver aquí tras entrar), y así no queda
      // en memoria nada de la cuenta borrada.
      window.location.replace('/login');
    } catch (error) {
      setDeleting(false);
      if (hasErrorCode(error, 'wrong_password')) {
        setPasswordError(describeError(error));
        return;
      }
      setFormError(describeError(error));
    }
  }

  return (
    <section
      aria-labelledby="danger-zone"
      className="rounded-2xl border border-danger/40 bg-danger-surface p-4"
    >
      <h2 id="danger-zone" className="flex items-center gap-2 text-base font-semibold text-danger">
        <TriangleAlert className="size-4" aria-hidden="true" />
        Zona de peligro
      </h2>
      <p className="mt-2 text-sm text-ink">
        Borrar la cuenta es <strong>irreversible</strong>. Se borra todo lo tuyo: perfil, medidas,
        ejercicios, rutinas, entrenamientos, alimentos, recetas, diario de comidas, objetivos,
        publicaciones, comentarios, likes, seguidores, fotos subidas y tokens de acceso. También se
        borra lo que este dispositivo tenga guardado sin conexión, aunque aún no se haya
        sincronizado.
      </p>
      <p className="mt-2 text-sm text-ink-muted">
        Lo que otras personas copiaron de ti (rutinas o recetas) es suyo y se queda, pero sin tus
        fotos.
      </p>

      {!hasPassword ? (
        // Sin contraseña no hay con qué confirmarlo: primero se pone una
        // (arriba, confirmando antes con el proveedor).
        <p className="mt-4 text-sm text-ink">
          Para borrar la cuenta, ponle antes una contraseña en «Poner contraseña»: es con la que
          confirmarás el borrado.
        </p>
      ) : confirming ? (
        <form
          onSubmit={(event) => void handleDelete(event)}
          noValidate
          className="mt-4 flex flex-col gap-3"
        >
          {formError ? (
            <ErrorBanner title="No se ha podido borrar la cuenta" message={formError} />
          ) : null}
          <Field
            htmlFor="deletePassword"
            label="Escribe tu contraseña para confirmar"
            error={passwordError ?? undefined}
          >
            <Input
              {...fieldAria('deletePassword', passwordError ?? undefined)}
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (passwordError !== null) setPasswordError(null);
              }}
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              variant="danger"
              loading={deleting}
              icon={<Trash2 className="size-4" />}
            >
              Borrar mi cuenta para siempre
            </Button>
            <Button variant="secondary" onClick={cancel} disabled={deleting}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : (
        <Button
          variant="danger"
          className="mt-4"
          onClick={() => setConfirming(true)}
          icon={<Trash2 className="size-4" />}
        >
          Borrar mi cuenta
        </Button>
      )}
    </section>
  );
}
